/**
 * Phrase normalization for grouping free-text judge output.
 *
 * The recommendation-category judge writes caveat reasons ("Too complex for
 * small teams"), audiences ("teams without a controller") and best-for
 * phrases ("multi-entity companies") in its own words, so the same idea
 * arrives in several wordings. `normalizePhrase` folds the surface
 * differences that do not change meaning — case, punctuation, filler words,
 * plural endings — so "Too complex for small teams." and "complex for a
 * small team" group together. Word order and negations ("no", "not",
 * "without") are kept: "No native Xero sync" stays apart from "native Xero
 * sync". Meaning-level merging (embeddings) is a later step.
 *
 * Pure: safe for `@repo/core/client`.
 */

/** Filler words dropped from a phrase. Negations are deliberately absent. */
const STOPWORDS = new Set([
	"a",
	"an",
	"the",
	"and",
	"or",
	"of",
	"for",
	"to",
	"in",
	"on",
	"at",
	"by",
	"with",
	"from",
	"as",
	"into",
	"its",
	"it",
	"is",
	"are",
	"be",
	"being",
	"been",
	"was",
	"were",
	"this",
	"that",
	"these",
	"those",
	"their",
	"your",
	"you",
	"our",
	"who",
	"which",
	"too",
	"very",
	"quite",
	"rather",
	"really",
	"somewhat",
	"can",
	"may",
	"might",
	"will",
	"would",
	"could",
	"should",
	"than",
	"so",
	"such",
	"some",
	"any",
]);

/** Words that end in "s" but are not plurals (kept as written). */
const S_EXCEPTIONS = new Set([
	"analytics",
	"business",
	"less",
	"news",
	"saas",
	"series",
	"always",
	"various",
	"across",
	"towards",
	"has",
	"was",
	"this",
	"its",
	"us",
	"plus",
	"status",
	"bus",
	"gas",
	"ios",
	"aws",
	"sms",
]);

/** Naive singular: "companies" → "company", "teams" → "team". */
export function singularize(word: string): string {
	if (word.length <= 3 || S_EXCEPTIONS.has(word)) {
		return word;
	}
	if (word.endsWith("ies") && word.length > 4) {
		return `${word.slice(0, -3)}y`;
	}
	if (word.endsWith("sses")) {
		return word.slice(0, -2);
	}
	if (
		word.endsWith("s") &&
		!word.endsWith("ss") &&
		!word.endsWith("us") &&
		!word.endsWith("is")
	) {
		return word.slice(0, -1);
	}
	return word;
}

/**
 * Normalize a free-text phrase to its grouping key: lowercase, diacritics
 * folded, punctuation removed, filler words dropped, naive singular. Returns
 * "" for empty input; a phrase made only of filler words keeps its words
 * (so it never collapses to "").
 */
export function normalizePhrase(text: string | null | undefined): string {
	if (!text) {
		return "";
	}
	const words = text
		.normalize("NFKD")
		.replace(/\p{M}+/gu, "")
		.toLowerCase()
		.replace(/['’‘`]/g, "")
		.replace(/[^\p{L}\p{N}]+/gu, " ")
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	if (words.length === 0) {
		return "";
	}
	const content = words.filter((w) => !STOPWORDS.has(w));
	return (content.length > 0 ? content : words).map(singularize).join(" ");
}
